import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import MuiChip, {
  ChipTypeMap,
  ChipProps as MuiChipProps,
} from '@mui/material/Chip';

import { Icon } from './Icon';

export type ChipVariant = 'soft' | 'outlined' | 'filled';

export type ChipProps<
  RootComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  AdditionalProps extends object = object,
> = Omit<MuiChipProps<RootComponent, AdditionalProps>, 'variant'> & {
  active?: boolean;
  variant?: ChipVariant;
  /** Passthrough for filter chips (not a MUI Chip prop). */
  value?: string | number;
  /**
   * React Router `Link` props when using `component={RouterLink}`.
   * (JSX does not infer `RootComponent`, so these are listed explicitly.)
   */
  to?: string;
  replace?: boolean;
  state?: unknown;
};

function ChipRoot(props: ChipProps<any>) {
  const {
    color = 'secondary',
    clickable,
    sx,
    variant = 'soft',
    active = false,
    ...otherProps
  } = props;

  const internalColor = active ? 'primary' : color;
  const internalVariant: ChipVariant = active ? 'soft' : variant;
  const muiVariant: 'filled' | 'outlined' =
    internalVariant === 'outlined' ? 'outlined' : 'filled';

  const styles = [
    internalVariant === 'outlined' && {
      borderColor: `${color}.borderStrong`,
      color: `${color}.text`,
    },
    {
      '& .MuiChip-deleteIcon': {
        fontSize: '16px',
        color: 'action.active',
      },
    },
    {
      '&. MuiChip-label': {
        display: 'flex',
        alignItems: 'center',
      },
    },
    {
      '& .MuiChip-icon': {
        marginLeft: '5px',
      },
    },
  ];

  return (
    <MuiChip
      sx={[...styles, ...(Array.isArray(sx) ? sx : [sx])]}
      variant={muiVariant}
      color={internalColor}
      clickable={clickable}
      deleteIcon={<Icon icon={faTimes} />}
      {...otherProps}
    ></MuiChip>
  );
}

export const Chip = ChipRoot as <
  RootComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  AdditionalProps extends object = object,
>(
  props: ChipProps<RootComponent, AdditionalProps>
) => React.ReactElement | null;
